import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
    Lock,
    Eye,
    EyeOff,
    CheckCircle,
    XCircle,
    Loader2,
    ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_member-hub-104/artifacts/mnjq1cak_Recovery%20Room%20Archive-07.png";

const MemberResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!token) {
            setIsLoading(false);
            setTokenValid(false);
            return;
        }

        // Verify the token
        const verifyToken = async () => {
            try {
                const response = await fetch(`${API_URL}/api/auth/member/verify-reset-token?token=${token}`);
                if (response.ok) {
                    const data = await response.json();
                    setTokenValid(true);
                    setTokenEmail(data.email);
                } else {
                    setTokenValid(false);
                }
            } catch (error) {
                setTokenValid(false);
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, [token]);

    const validateForm = () => {
        const newErrors = {};
        
        if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }
        
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/api/auth/member/reset-confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: token,
                    new_password: formData.password
                })
            });

            if (response.ok) {
                setResetSuccess(true);
                toast.success('Password reset successful!');
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to reset password');
            }
        } catch (error) {
            toast.error('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-[#C9A227] animate-spin mx-auto mb-4" />
                    <p className="text-[#A0A0A0]">Verifying your reset link...</p>
                </div>
            </div>
        );
    }

    // Invalid/expired token
    if (!tokenValid) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex flex-col">
                <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/90 backdrop-blur-lg border-b border-[#5A6350]">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                        <Link to="/join" className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="Recovery Room Logo" className="w-10 h-10 object-contain" />
                            <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        </Link>
                    </div>
                </header>

                <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-red-500 to-red-400" />
                        <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-8 h-8 text-red-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Invalid or Expired Link</h2>
                            <p className="text-[#A0A0A0] mb-6">
                                This password reset link is invalid or has expired. Please request a new one.
                            </p>
                            <Link to="/member/login">
                                <Button className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold">
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                    Back to Login
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Success state
    if (resetSuccess) {
        return (
            <div className="min-h-screen bg-[#3D4435] flex flex-col">
                <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/90 backdrop-blur-lg border-b border-[#5A6350]">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                        <Link to="/join" className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="Recovery Room Logo" className="w-10 h-10 object-contain" />
                            <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                        </Link>
                    </div>
                </header>

                <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                        <CardContent className="p-6 text-center">
                            <div className="w-16 h-16 bg-[#C9A227]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-[#C9A227]" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-2">Password Reset Complete</h2>
                            <p className="text-[#A0A0A0] mb-6">
                                Your password has been successfully reset. You can now log in with your new password.
                            </p>
                            <Link to="/member/login">
                                <Button className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold" data-testid="go-to-login-btn">
                                    Go to Login
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Reset form
    return (
        <div className="min-h-screen bg-[#3D4435] flex flex-col">
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#3D4435]/90 backdrop-blur-lg border-b border-[#5A6350]">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/join" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Recovery Room Logo" className="w-10 h-10 object-contain" />
                        <h1 className="text-xl font-bold text-white font-['Playfair_Display']">Recovery Room</h1>
                    </Link>
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 pt-24 pb-12">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
                            Create New Password
                        </h2>
                        <p className="text-[#A0A0A0]">
                            Enter a new password for <span className="text-[#C9A227]">{tokenEmail}</span>
                        </p>
                    </div>

                    <Card className="bg-[#4A5240] border-[#5A6350] shadow-2xl overflow-hidden">
                        <div className="h-1 bg-gradient-to-r from-[#C9A227] to-[#E8D48A]" />
                        <CardContent className="p-6">
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-white flex items-center gap-2">
                                        <Lock className="w-4 h-4 text-[#C9A227]" />
                                        New Password
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, password: e.target.value }));
                                                setErrors(prev => ({ ...prev, password: '' }));
                                            }}
                                            placeholder="Enter new password"
                                            className={`bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                            data-testid="new-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-red-400 text-sm">{errors.password}</p>
                                    )}
                                    <p className="text-[#666] text-xs">Must be at least 8 characters</p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-white flex items-center gap-2">
                                        <Lock className="w-4 h-4 text-[#C9A227]" />
                                        Confirm Password
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.confirmPassword}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
                                                setErrors(prev => ({ ...prev, confirmPassword: '' }));
                                            }}
                                            placeholder="Confirm new password"
                                            className={`bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                                            data-testid="confirm-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="text-red-400 text-sm">{errors.confirmPassword}</p>
                                    )}
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                    data-testid="reset-password-submit-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-[#5A6350]">
                                <Link 
                                    to="/member/login" 
                                    className="flex items-center justify-center gap-2 text-[#A0A0A0] hover:text-white text-sm transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Login
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default MemberResetPasswordPage;
